import { Controller } from "@hotwired/stimulus"
import Cropper from "cropperjs"

export default class extends Controller {
  static targets = ['image', 'toggleButton', 'cancelButton', 'dimensions', 'cropX', 'cropY', 'cropWidth', 'cropHeight']

  connect() {
    this.cropper = null
    this.processedImageSrc = null
  }

  disconnect() {
    this.destroy()
  }

  toggle(event) {
    event.currentTarget.blur()

    if (this.cropper) {
      this.apply()
      this.destroy()
      this.updateButtonStates(false)
    } else {
      this.setCropper()
      this.updateButtonStates(true)
    }
  }

  cancel() {
    this.destroy()
    this.updateButtonStates(false)
  }

  setCropper() {
    this.destroy()

    // Store the processed image src to restore later
    this.processedImageSrc = this.imageTarget.src

    // Load the original unprocessed image for cropping
    this.imageTarget.src = this.imageTarget.dataset.originalUrl

    this.cropper = new Cropper(this.imageTarget, { container: '.cropper-container' })

    const cropperImage = this.cropper.getCropperImage()
    const cropperCanvas = this.cropper.getCropperCanvas()
    const cropperSelection = this.cropper.getCropperSelection()

    cropperCanvas.style.minWidth = '300px'
    cropperCanvas.style.minHeight = '450px'

    // Wait for image to be ready
    cropperImage.$ready(() => {
      this.setupCropperConstraints(cropperImage, cropperCanvas, cropperSelection)

      const placeInitialSelection = () => {
        const imageRect = cropperImage.getBoundingClientRect()
        const canvasRect = cropperCanvas.getBoundingClientRect()

        const initialWidth = 200
        const initialHeight = 200

        const offsetX = imageRect.left - canvasRect.left
        const offsetY = imageRect.top - canvasRect.top

        const x = offsetX + Math.max(0, Math.floor((imageRect.width - initialWidth) / 2))
        const y = offsetY + Math.max(0, Math.floor((imageRect.height - initialHeight) / 2))

        cropperSelection.$change(x, y, initialWidth, initialHeight)
      }

      // Defer to avoid being overridden by the default initial coverage sizing
      requestAnimationFrame(placeInitialSelection)
    })
  }

  setupCropperConstraints(cropperImage, cropperCanvas, cropperSelection) {
    // Helper function to check if selection is within boundaries
    const inSelection = (selection, maxSelection) => {
      return (
        selection.x >= maxSelection.x &&
        selection.y >= maxSelection.y &&
        (selection.x + selection.width) <= (maxSelection.x + maxSelection.width) &&
        (selection.y + selection.height) <= (maxSelection.y + maxSelection.height)
      )
    }

    // Helper to calculate image boundaries relative to canvas
    const getMaxSelection = (imageRect) => {
      const canvasRect = cropperCanvas.getBoundingClientRect()
      return {
        x: imageRect.left - canvasRect.left,
        y: imageRect.top - canvasRect.top,
        width: imageRect.width,
        height: imageRect.height
      }
    }

    // Restrict image transforms (zoom/pan) to keep selection within bounds
    cropperImage.addEventListener('transform', (event) => {
      const cropperImageClone = cropperImage.cloneNode()
      cropperImageClone.style.transform = `matrix(${event.detail.matrix.join(', ')})`
      cropperImageClone.style.opacity = '0'
      cropperCanvas.appendChild(cropperImageClone)

      const maxSelection = getMaxSelection(cropperImageClone.getBoundingClientRect())
      cropperCanvas.removeChild(cropperImageClone)

      if (!inSelection(cropperSelection, maxSelection)) {
        event.preventDefault()
      }
    })

    // Restrict selection changes to stay within image boundaries
    cropperSelection.addEventListener('change', (event) => {
      const maxSelection = getMaxSelection(cropperImage.getBoundingClientRect())

      if (!inSelection(event.detail, maxSelection)) {
        event.preventDefault()
      }

      this.updateDimensions(event.detail, cropperImage)
    })
  }

  updateDimensions(selection, cropperImage) {
    if (!this.hasDimensionsTarget) return

    const imgElement = cropperImage.querySelector('img') || this.imageTarget
    const naturalWidth = imgElement.naturalWidth
    const naturalHeight = imgElement.naturalHeight

    const cropperImageRect = cropperImage.getBoundingClientRect()

    const scaleX = naturalWidth / cropperImageRect.width
    const scaleY = naturalHeight / cropperImageRect.height

    const pixelWidth = Math.round(selection.width * scaleX)
    const pixelHeight = Math.round(selection.height * scaleY)

    this.dimensionsTarget.textContent = `${pixelWidth} × ${pixelHeight} px`
  }

  apply() {
    const selection = this.cropper.getCropperSelection()
    const cropperImage = this.cropper.getCropperImage()
    const cropperCanvas = this.cropper.getCropperCanvas()

    if (!selection) return

    const coordinates = this.calculateCropCoordinates(selection, cropperImage, cropperCanvas)

    // Update hidden form fields
    this.cropXTarget.value = coordinates.x
    this.cropYTarget.value = coordinates.y
    this.cropWidthTarget.value = coordinates.width
    this.cropHeightTarget.value = coordinates.height

    // Update preview with cropped image
    this.updatePreview(coordinates)
    this.dimensionsTarget.classList.add('hidden')
  }

  calculateCropCoordinates(selection, cropperImage, cropperCanvas) {
    // Get actual rendered dimensions
    const imgElement = cropperImage.querySelector('img') || this.imageTarget
    const naturalWidth = imgElement.naturalWidth
    const naturalHeight = imgElement.naturalHeight

    // Get the actual displayed size using getBoundingClientRect
    const cropperCanvasRect = cropperCanvas.getBoundingClientRect()
    const cropperImageRect = cropperImage.getBoundingClientRect()

    // Calculate image position and size relative to canvas
    const imageDisplayWidth = cropperImageRect.width
    const imageDisplayHeight = cropperImageRect.height
    const imageOffsetX = cropperImageRect.left - cropperCanvasRect.left
    const imageOffsetY = cropperImageRect.top - cropperCanvasRect.top

    // Calculate scale ratios
    const scaleX = naturalWidth / imageDisplayWidth
    const scaleY = naturalHeight / imageDisplayHeight

    // Convert selection coordinates from canvas space to image pixel space
    const imageX = (selection.x - imageOffsetX) * scaleX
    const imageY = (selection.y - imageOffsetY) * scaleY
    const imageWidth = selection.width * scaleX
    const imageHeight = selection.height * scaleY

    return {
      x: Math.round(imageX),
      y: Math.round(imageY),
      width: Math.round(imageWidth),
      height: Math.round(imageHeight)
    }
  }

  updatePreview(coordinates) {
    // Load the original image to get full resolution data
    const originalImage = new Image()
    originalImage.crossOrigin = 'anonymous'

    originalImage.onload = () => {
      // Create a canvas with the actual cropped area from the original image
      const canvas = document.createElement('canvas')
      canvas.width = coordinates.width
      canvas.height = coordinates.height

      const ctx = canvas.getContext('2d')

      // Draw the cropped portion from the original image
      ctx.drawImage(
        originalImage,
        coordinates.x, coordinates.y, coordinates.width, coordinates.height,
        0, 0, coordinates.width, coordinates.height
      )

      // Update preview with the cropped image at full resolution
      this.imageTarget.src = canvas.toDataURL()
    }

    // Use the original image URL from the dataset
    originalImage.src = this.imageTarget.dataset.originalUrl || this.imageTarget.src
  }

  updateButtonStates(isCropping) {
    if (this.hasToggleButtonTarget) {
      this.toggleButtonTarget.textContent = isCropping ? 'Apply Crop' : 'Crop'
    }

    if (this.hasCancelButtonTarget) {
      if (isCropping) {
        this.cancelButtonTarget.classList.remove('hidden')
      } else {
        this.cancelButtonTarget.classList.add('hidden')
      }
    }

    if (this.hasDimensionsTarget) {
      if (isCropping) {
        this.dimensionsTarget.classList.remove('hidden')
      } else {
        this.dimensionsTarget.classList.add('hidden')
      }
    }
  }

  destroy() {
    if (this.cropper) {
      this.cropper.destroy()
      this.cropper = null

      // Restore the processed image
      if (this.processedImageSrc) {
        this.imageTarget.src = this.processedImageSrc
      }
    }

    // Hide dimensions overlay
    if (this.hasDimensionsTarget) {
      this.dimensionsTarget.classList.add('hidden')
    }
  }
}
